import { useMemo, useState } from "react";
import SiteHeader from "@/components/feature/SiteHeader";
import SiteFooter from "@/components/feature/SiteFooter";
import PageHeader from "@/components/base/PageHeader";
import EmptyState from "@/components/base/EmptyState";
import Pagination from "@/components/base/Pagination";
import VideoCard from "@/components/feature/VideoCard";
import VideoFilterPanel from "./components/VideoFilterPanel";
import { videos } from "@/mocks/videos";
import usePageMeta from "@/hooks/usePageMeta";
import type { VideoCategory } from "@/types/content";
import type { DurationKey, SortKey } from "./components/VideoFilterPanel";

const PAGE_SIZE = 6;

const allTags = Array.from(new Set(videos.flatMap((item) => item.tags)));

export default function VideosPage() {
  usePageMeta(
    "挠痒视频 - 挠脚心网站",
    "浏览挠脚心网站精选挠痒视频，覆盖经典挠痒、搞笑挠痒与互动挠痒三类内容，享受轻松有趣的观看体验。",
    "挠痒视频,挠脚心视频,搞笑挠痒,互动挠痒,经典挠痒"
  );

  const [category, setCategory] = useState<string>("全部");
  const [sort, setSort] = useState<SortKey>("latest");
  const [duration, setDuration] = useState<DurationKey>("all");
  const [activeTags, setActiveTags] = useState<string[]>([]);
  const [page, setPage] = useState(1);
  const [filterOpen, setFilterOpen] = useState(false);

  const reset = () => {
    setCategory("全部");
    setSort("latest");
    setDuration("all");
    setActiveTags([]);
    setPage(1);
  };

  const filtered = useMemo(() => {
    let list = videos.filter((item) => {
      if (category !== "全部" && item.category !== (category as VideoCategory)) return false;
      if (duration === "short" && item.duration >= 180) return false;
      if (duration === "medium" && (item.duration < 180 || item.duration > 360)) return false;
      if (duration === "long" && item.duration <= 360) return false;
      if (activeTags.length > 0 && !activeTags.some((tag) => item.tags.includes(tag))) return false;
      return true;
    });

    list = list.sort((a, b) => {
      if (sort === "hot") return b.heat - a.heat;
      return a.date < b.date ? 1 : -1;
    });

    return list;
  }, [category, sort, duration, activeTags]);

  const totalPages = Math.ceil(filtered.length / PAGE_SIZE);
  const currentPage = Math.min(page, Math.max(1, totalPages));
  const visible = filtered.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  const handleTagToggle = (tag: string) => {
    setPage(1);
    setActiveTags((prev) =>
      prev.includes(tag) ? prev.filter((item) => item !== tag) : [...prev, tag]
    );
  };

  return (
    <div className="flex min-h-screen w-full flex-col bg-background-50">
      <SiteHeader />
      <main className="flex-1">
        <PageHeader
          crumbs={[{ label: "首页", to: "/" }, { label: "挠痒视频" }]}
          kicker="Video Collection"
          title="挠痒视频"
          description="精选的轻挠、爆笑与互动挑战短片，从三分钟到十五分钟都有。所有视频仅展示封面预览，不提供真实播放。"
          aside={
            <div className="flex items-center gap-3 rounded-lg border border-background-200 bg-background-50 px-4 py-3">
              <span className="flex h-9 w-9 items-center justify-center rounded-md bg-primary-100 text-primary-800">
                <i className="ri-film-line text-base"></i>
              </span>
              <div>
                <p className="font-heading text-lg leading-none text-foreground-950">{videos.length}</p>
                <p className="mt-1 text-[11px] text-foreground-500">个演示视频</p>
              </div>
            </div>
          }
        />

        <div className="mx-auto w-full max-w-[1240px] px-4 py-8 md:px-6 md:py-10">
          <VideoFilterPanel
            category={category}
            onCategoryChange={(value) => {
              setCategory(value);
              setPage(1);
            }}
            sort={sort}
            onSortChange={setSort}
            duration={duration}
            onDurationChange={(value) => {
              setDuration(value);
              setPage(1);
            }}
            tags={allTags}
            activeTags={activeTags}
            onTagToggle={handleTagToggle}
            onReset={reset}
            open={filterOpen}
            onToggle={() => setFilterOpen((prev) => !prev)}
            resultCount={filtered.length}
          />

          <div className="mt-6">
            {visible.length > 0 ? (
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                {visible.map((video) => (
                  <VideoCard key={video.id} video={video} />
                ))}
              </div>
            ) : (
              <EmptyState
                icon="ri-film-line"
                title="抱歉，未找到符合条件的视频"
                description="试着放宽时长范围，或者减少几个标签，再回来看看。"
                actionLabel="清空筛选条件"
                onAction={reset}
              />
            )}
          </div>

          <Pagination page={currentPage} totalPages={totalPages} onChange={setPage} />
        </div>
      </main>
      <SiteFooter />
    </div>
  );
}